import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { HelpCircle, Loader2, RefreshCw, RotateCcw, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

import { CreateJiraProjectButton } from "@/components/create-jira-project-button";
import { LinkJiraProjectButton } from "@/components/link-jira-project-button";
import { PMNoteAttachmentList } from "@/components/pm-note-attachments";
import { ProjectObjectiveChip } from "@/components/project-objective-chip";
import { formatStatusLabel, StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/hooks/use-auth";
import { listDelivery } from "@/lib/api/feature-requests";
import { cn } from "@/lib/cn";
import { DIRECTORATES, STRATEGIC_PILLARS } from "@/lib/constants";
import {
  DELIVERY_HEALTH,
  DELIVERY_STAGES,
  daysUntil,
  deliveryDeadlineOf,
  deliveryHealthLabel,
  deliveryHealthOf,
  deliveryStageHelp,
  deliveryStageIndex,
  deliveryStageLabel,
  deliveryStageOf,
  deliveryTitleOf,
  releaseVsDeadline,
} from "@/lib/delivery-stages";
import { formatDate, formatDateTime } from "@/lib/format";
import {
  displayName,
  type DeliveryHealth,
  type DeliveryStage,
  type FeatureRequest,
  type FeatureRequestEvent,
  type FeatureRequestStatus,
  type PMNote,
} from "@/types";

const FILTER_ANY = "__any__";
const FILTER_NONE = "__none__";
const SYNC_KEY = "lio-quest:delivery-last-sync";

const searchSchema = z.object({
  expand: z.coerce.number().int().positive().optional(),
});

export const Route = createFileRoute("/_app/requests/delivery")({
  validateSearch: (search) => searchSchema.parse(search),
  component: DeliveryBoardPage,
});

type SortOrder = "asc" | "desc";

function DeliveryBoardPage() {
  const { user } = useAuth();
  const { expand: expandFromSearch } = Route.useSearch();
  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  const [directorate, setDirectorate] = useState("");
  const [pillar, setPillar] = useState("");
  const [pmFilter, setPmFilter] = useState("");
  const [stages, setStages] = useState<Set<DeliveryStage>>(new Set());
  const [health, setHealth] = useState<"" | DeliveryHealth>("");
  const [deadlineChip, setDeadlineChip] = useState("");
  const [dueBy, setDueBy] = useState("");
  const [ceoOnly, setCeoOnly] = useState(false);
  const [adhocOnly, setAdhocOnly] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [sort, setSort] = useState<SortOrder>("asc");
  const [expandedId, setExpandedId] = useState<number | null>(expandFromSearch ?? null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(SYNC_KEY);
    } catch {
      return null;
    }
  });

  const listQuery = useQuery({
    queryKey: ["feature-requests", "delivery"],
    queryFn: listDelivery,
  });
  const items = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  const pmOptions = useMemo(() => {
    const names = new Map<string, string>();
    for (const r of items) {
      if (r.pm_in_charge) {
        const name = displayName(r.pm_in_charge, r.pm_in_charge_id ?? undefined);
        names.set(name, name);
      }
    }
    return [...names.keys()].sort((a, b) => a.localeCompare(b));
  }, [items]);

  const baseFiltered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const today = new Date();
    const local = new Date(today.getTime() - today.getTimezoneOffset() * 60_000)
      .toISOString()
      .slice(0, 10);
    return items.filter((r) => {
      if (
        needle &&
        !deliveryTitleOf(r).toLowerCase().includes(needle) &&
        !r.title.toLowerCase().includes(needle) &&
        !(r.jira_issue_key ?? "").toLowerCase().includes(needle)
      ) {
        return false;
      }
      if (directorate && r.directorate !== directorate) return false;
      if (pillar && (r.project_objective ?? "") !== pillar) return false;
      const pmName = r.pm_in_charge
        ? displayName(r.pm_in_charge, r.pm_in_charge_id ?? undefined)
        : "";
      if (pmFilter === FILTER_NONE && pmName) return false;
      if (pmFilter && pmFilter !== FILTER_NONE && pmName !== pmFilter) return false;
      if (ceoOnly && !r.ceo_highlight) return false;
      if (adhocOnly && !r.adhoc) return false;
      if (mineOnly && user && r.requester_id !== user.id) return false;
      const dl = deliveryDeadlineOf(r);
      if (deadlineChip && dl !== deadlineChip) return false;
      if (dueBy && (!dl || dl > dueBy)) return false;
      if (deadlineChip || dueBy) {
        /* keep */
      } else if (!deadlineChip && !dueBy) {
        /* ok */
      }
      void local;
      return true;
    });
  }, [
    items,
    q,
    directorate,
    pillar,
    pmFilter,
    ceoOnly,
    adhocOnly,
    mineOnly,
    user,
    deadlineChip,
    dueBy,
  ]);

  const upcomingDeadlines = useMemo(() => {
    const today = new Date();
    const local = new Date(today.getTime() - today.getTimezoneOffset() * 60_000)
      .toISOString()
      .slice(0, 10);
    return [
      ...new Set(
        items
          .filter((r) => deliveryStageOf(r) !== "released")
          .map(deliveryDeadlineOf)
          .filter((d) => d && d >= local),
      ),
    ]
      .sort()
      .slice(0, 2);
  }, [items]);

  const healthCounts = useMemo(() => {
    const scoped = baseFiltered.filter(
      (r) => !stages.size || stages.has(deliveryStageOf(r)),
    );
    const counts: Record<string, number> = {
      on_track: 0,
      at_risk: 0,
      off_track: 0,
      overdue: 0,
    };
    for (const r of scoped) {
      const h = deliveryHealthOf(r);
      if (h === "done") continue;
      counts[h] = (counts[h] ?? 0) + 1;
    }
    return counts;
  }, [baseFiltered, stages]);

  const stageCounts = useMemo(() => {
    const scoped = baseFiltered.filter(
      (r) => !health || deliveryHealthOf(r) === health,
    );
    const counts: Record<string, number> = {};
    for (const s of DELIVERY_STAGES) counts[s.key] = 0;
    for (const r of scoped) {
      const st = deliveryStageOf(r);
      counts[st] = (counts[st] ?? 0) + 1;
    }
    return counts;
  }, [baseFiltered, health]);

  const rows = useMemo(() => {
    const filtered = baseFiltered.filter((r) => {
      if (health && deliveryHealthOf(r) !== health) return false;
      if (stages.size && !stages.has(deliveryStageOf(r))) return false;
      return true;
    });
    // Deep-link expand: keep the target row visible even if filters exclude it.
    if (
      expandFromSearch &&
      !filtered.some((r) => r.id === expandFromSearch)
    ) {
      const hit = items.find((r) => r.id === expandFromSearch);
      if (hit) filtered.unshift(hit);
    }
    return filtered.sort((a, b) => {
      const aRel = deliveryStageOf(a) === "released" ? 1 : 0;
      const bRel = deliveryStageOf(b) === "released" ? 1 : 0;
      if (aRel !== bRel) return aRel - bRel;
      const x = deliveryDeadlineOf(a) || "9999";
      const y = deliveryDeadlineOf(b) || "9999";
      return sort === "asc" ? x.localeCompare(y) : y.localeCompare(x);
    });
  }, [baseFiltered, health, stages, sort, expandFromSearch, items]);

  useEffect(() => {
    if (!expandFromSearch) return;
    setExpandedId(expandFromSearch);
    const timer = window.setTimeout(() => {
      document
        .getElementById(`dev-${expandFromSearch}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [expandFromSearch, rows]);

  const activeCount = items.filter((r) => deliveryStageOf(r) !== "released").length;
  const hasFilters =
    Boolean(q.trim()) ||
    Boolean(directorate) ||
    Boolean(pillar) ||
    Boolean(pmFilter) ||
    stages.size > 0 ||
    Boolean(health) ||
    Boolean(deadlineChip) ||
    Boolean(dueBy) ||
    ceoOnly ||
    adhocOnly ||
    mineOnly;

  const clearFilters = () => {
    setQ("");
    setDirectorate("");
    setPillar("");
    setPmFilter("");
    setStages(new Set());
    setHealth("");
    setDeadlineChip("");
    setDueBy("");
    setCeoOnly(false);
    setAdhocOnly(false);
    setMineOnly(false);
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ["feature-requests", "delivery"] });
      await listQuery.refetch();
      const now = new Date().toISOString();
      setLastSynced(now);
      try {
        window.localStorage.setItem(SYNC_KEY, now);
      } catch {
        /* ignore */
      }
    } finally {
      setSyncing(false);
    }
  };

  const statusBtnLabel =
    !stages.size || stages.size === DELIVERY_STAGES.length
      ? "Status: All"
      : stages.size === 1
        ? `Status: ${deliveryStageLabel([...stages][0])}`
        : `Status: ${stages.size} selected`;

  return (
    <div className="grid min-w-0 gap-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-heading text-xl leading-tight font-semibold tracking-tight">
            Delivery board
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Every request approved to Proceed, from queue to release. Status, health and deadline
            come from Jira (same fields as Project Monitoring).
          </p>
          <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={syncing}
              onClick={() => void syncAll()}
            >
              <RefreshCw className={cn("size-3.5", syncing && "animate-spin")} />
              {syncing ? "Syncing…" : "Sync all from Jira"}
            </Button>
            <span className="text-xs text-muted-foreground">
              {lastSynced
                ? `Last synced ${formatDateTime(lastSynced)}`
                : "Not synced yet — open refreshes live from Jira"}
            </span>
            {activeCount > 0 ? (
              <span className="text-xs text-muted-foreground">{activeCount} active</span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-52">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search initiatives"
              className="pl-9"
              aria-label="Search initiatives on the delivery board"
            />
          </div>
          <Select
            value={directorate || FILTER_ANY}
            onValueChange={(v) => setDirectorate(v === FILTER_ANY ? "" : v)}
          >
            <SelectTrigger className="w-full sm:w-40" aria-label="Filter by directorate">
              <SelectValue placeholder="All directorates" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FILTER_ANY}>All directorates</SelectItem>
              {DIRECTORATES.map((d) => (
                <SelectItem key={d} value={d}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={pillar || FILTER_ANY}
            onValueChange={(v) => setPillar(v === FILTER_ANY ? "" : v)}
          >
            <SelectTrigger className="w-full sm:w-40" aria-label="Filter by strategic pillar">
              <SelectValue placeholder="All pillars" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FILTER_ANY}>All pillars</SelectItem>
              {STRATEGIC_PILLARS.map((p) => (
                <SelectItem key={p.key} value={p.key}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={pmFilter || FILTER_ANY}
            onValueChange={(v) => setPmFilter(v === FILTER_ANY ? "" : v)}
          >
            <SelectTrigger className="w-full sm:w-40" aria-label="Filter by PM in charge">
              <SelectValue placeholder="All PMs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FILTER_ANY}>All PMs</SelectItem>
              <SelectItem value={FILTER_NONE}>Not assigned</SelectItem>
              {pmOptions.map((name) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(
                "h-8",
                stages.size > 0 && stages.size < DELIVERY_STAGES.length && "border-primary text-primary",
              )}
              aria-haspopup="true"
              aria-expanded={statusOpen}
              onClick={() => setStatusOpen((v) => !v)}
            >
              {statusBtnLabel}
            </Button>
            {statusOpen ? (
              <>
                <button
                  type="button"
                  className="fixed inset-0 z-40 cursor-default"
                  aria-label="Close status filter"
                  onClick={() => setStatusOpen(false)}
                />
                <div
                  className="absolute top-full right-0 z-50 mt-1 max-h-80 w-[min(100vw-2rem,22rem)] overflow-auto rounded-lg border border-border bg-card p-2 shadow-lg"
                  role="group"
                  aria-label="Delivery status"
                >
                  {DELIVERY_STAGES.map((s) => {
                    const checked = stages.has(s.key);
                    const n = stageCounts[s.key] ?? 0;
                    return (
                      <label
                        key={s.key}
                        title={s.help}
                        className={cn(
                          "flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60",
                          n === 0 && "opacity-50",
                        )}
                      >
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={checked}
                          onChange={() => {
                            setStages((prev) => {
                              const next = new Set(prev);
                              if (next.has(s.key)) next.delete(s.key);
                              else next.add(s.key);
                              return next;
                            });
                          }}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block font-medium">{s.label}</span>
                          <span className="block text-[11px] text-muted-foreground">{s.help}</span>
                        </span>
                        <span className="font-mono text-xs text-muted-foreground tabular-nums">
                          {n}
                        </span>
                      </label>
                    );
                  })}
                  <div className="mt-1 flex gap-2 border-t border-border pt-2">
                    <button
                      type="button"
                      className="text-xs font-medium text-primary"
                      onClick={() => setStages(new Set(DELIVERY_STAGES.map((s) => s.key)))}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="text-xs font-medium text-muted-foreground"
                      onClick={() => setStages(new Set())}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </>
            ) : null}
          </div>

          <Select value={sort} onValueChange={(v) => setSort(v as SortOrder)}>
            <SelectTrigger className="w-full sm:w-48" aria-label="Order by deadline">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">Deadline: nearest first</SelectItem>
              <SelectItem value="desc">Deadline: latest first</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div
        className="grid grid-cols-2 overflow-hidden rounded-xl bg-card shadow-xs ring-1 ring-foreground/10 min-[900px]:grid-cols-4"
        role="group"
        aria-label="Project health"
      >
        {DELIVERY_HEALTH.map((h) => {
          const active = health === h.key;
          return (
            <button
              key={h.key}
              type="button"
              title={h.help}
              aria-pressed={active}
              onClick={() => setHealth((cur) => (cur === h.key ? "" : h.key))}
              className={cn(
                "border-r border-t-4 border-border px-3.5 py-3 text-left last:border-r-0 hover:bg-muted/40",
                h.key === "on_track" && "border-t-success",
                h.key === "at_risk" && "border-t-warning",
                h.key === "off_track" && "border-t-destructive",
                h.key === "overdue" && "border-t-primary",
                h.key === "on_track" && active && "bg-success-background",
                h.key === "at_risk" && active && "bg-warning-background",
                h.key === "off_track" && active && "bg-destructive/10",
                h.key === "overdue" && active && "bg-primary/10",
                active && "shadow-[inset_0_-3px_0_var(--primary)]",
              )}
            >
              <span
                className={cn(
                  "block font-heading text-2xl leading-none font-semibold tabular-nums",
                  h.key === "on_track" && "text-success",
                  h.key === "at_risk" && "text-warning",
                  h.key === "off_track" && "text-destructive",
                  h.key === "overdue" && "text-primary",
                )}
              >
                {healthCounts[h.key] ?? 0}
              </span>
              <span
                className={cn(
                  "mt-1.5 block text-xs font-semibold tracking-wide uppercase",
                  active && "text-primary",
                )}
              >
                {h.label}
              </span>
              <span
                className={cn(
                  "mt-0.5 block text-[11px] text-muted-foreground",
                  active && "text-primary/80",
                )}
              >
                {h.help}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
            Deadline
          </span>
          <FilterChip
            label="Any"
            pressed={!deadlineChip && !dueBy}
            onClick={() => {
              setDeadlineChip("");
              setDueBy("");
            }}
          />
          {upcomingDeadlines.map((d) => (
            <FilterChip
              key={d}
              label={formatDate(d)}
              pressed={deadlineChip === d}
              onClick={() => {
                setDeadlineChip((cur) => (cur === d ? "" : d));
                setDueBy("");
              }}
            />
          ))}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Due by</span>
            <DatePicker
              value={dueBy}
              onChange={(v) => {
                setDueBy(v);
                setDeadlineChip("");
              }}
              placeholder="Pick a date"
              className="w-[10.5rem]"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
            Flags
          </span>
          <FilterChip label="CEO highlight" pressed={ceoOnly} onClick={() => setCeoOnly((v) => !v)} />
          <FilterChip label="Ad-hoc" pressed={adhocOnly} onClick={() => setAdhocOnly((v) => !v)} />
          <FilterChip
            label="Submitted by me"
            pressed={mineOnly}
            onClick={() => setMineOnly((v) => !v)}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span>
          {rows.length} of {items.length} initiatives
        </span>
        {hasFilters ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 font-medium text-primary"
            onClick={clearFilters}
          >
            <RotateCcw className="size-3" /> Clear filters
          </button>
        ) : null}
      </div>

      {listQuery.isLoading ? (
        <div className="rounded-xl bg-card px-4 py-10 ring-1 ring-foreground/10" aria-busy="true">
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading delivery board…
          </p>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card px-5 py-14 text-center">
          <p className="font-heading text-lg font-semibold tracking-tight">
            Nothing on the delivery board yet
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Requests appear here once a PM decides Proceed.
          </p>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card px-5 py-14 text-center">
          <p className="font-heading text-lg font-semibold tracking-tight">No matching initiatives</p>
          <p className="mt-1 text-sm text-muted-foreground">Clear some filters to see more.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl bg-card shadow-xs ring-1 ring-foreground/10">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] border-collapse text-left text-sm">
              <thead>
                <tr>
                  <Th>Initiative</Th>
                  <Th>Directorate</Th>
                  <Th>PM in charge</Th>
                  <Th>
                    <span className="inline-flex items-center gap-1">
                      Delivery status
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label="What each delivery status means"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <HelpCircle className="size-3.5" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          align="start"
                          side="bottom"
                          sideOffset={8}
                          className="w-[min(100vw-2rem,24rem)] p-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <strong className="text-xs tracking-wide uppercase">
                            Delivery status stages
                          </strong>
                          <ol className="mt-2 grid gap-2">
                            {DELIVERY_STAGES.map((s) => (
                              <li
                                key={s.key}
                                className="grid grid-cols-[9.5rem_minmax(0,1fr)] items-start gap-2.5 text-[13px]"
                              >
                                <StageChip stage={s.key} />
                                <span className="text-muted-foreground">{s.help}</span>
                              </li>
                            ))}
                          </ol>
                        </PopoverContent>
                      </Popover>
                    </span>
                  </Th>
                  <Th>Health</Th>
                  <Th>Deadline</Th>
                  <Th>Actual release date</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <DeliveryRow
                    key={item.id}
                    item={item}
                    expanded={expandedId === item.id}
                    onToggle={() =>
                      setExpandedId((cur) => (cur === item.id ? null : item.id))
                    }
                    onSynced={() => void syncAll()}
                    syncing={syncing}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({
  label,
  pressed,
  onClick,
}: {
  label: string;
  pressed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
        pressed
          ? "border-primary bg-primary/10 text-primary"
          : "border-border bg-card text-muted-foreground hover:bg-muted/50",
      )}
    >
      {label}
    </button>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="relative border-b border-border px-3 py-2.5 text-xs font-medium whitespace-nowrap text-muted-foreground"
    >
      {children}
    </th>
  );
}

function StageChip({ stage }: { stage: DeliveryStage | string }) {
  const key = (stage === "queued" ? "queue" : stage) as DeliveryStage | string;
  return (
    <span
      title={`${deliveryStageLabel(stage)}: ${deliveryStageHelp(stage)}`}
      className={cn(
        "inline-flex h-[22px] items-center rounded-full border px-2 text-[11px] font-semibold whitespace-nowrap",
        key === "queue" && "border-border bg-muted text-foreground",
        (key === "prd_design" ||
          key === "technical_analysis" ||
          key === "ready_dev" ||
          key === "requirement" ||
          key === "design") &&
          "border-transparent bg-warning-background text-warning",
        (key === "in_dev" ||
          key === "ready_qa" ||
          key === "in_qa" ||
          key === "regression") &&
          "border-transparent bg-red-wash text-destructive",
        key === "released" && "border-transparent bg-success-background text-success",
      )}
    >
      {deliveryStageLabel(stage)}
    </span>
  );
}

function StageStepper({ stage }: { stage: DeliveryStage | string }) {
  const idx = deliveryStageIndex(stage);
  const released = stage === "released";
  return (
    <span
      className={cn("mt-1.5 flex gap-0.5", released && "opacity-70")}
      title={`${idx} of ${DELIVERY_STAGES.length - 1} stages`}
      aria-hidden
    >
      {DELIVERY_STAGES.slice(1).map((_, n) => (
        <i
          key={n}
          className={cn(
            "inline-block h-1.5 w-3 rounded-[1px]",
            n < idx ? "bg-primary" : "bg-border",
          )}
        />
      ))}
    </span>
  );
}

function HealthPill({ health }: { health: DeliveryHealth | string }) {
  return (
    <span
      className={cn(
        "inline-flex h-[22px] items-center gap-1.5 rounded-full px-2 text-[11px] font-semibold whitespace-nowrap",
        health === "on_track" && "bg-success-background text-success",
        health === "at_risk" && "bg-warning-background text-warning",
        health === "off_track" && "bg-destructive/15 text-destructive",
        health === "overdue" && "bg-destructive text-destructive-foreground",
        health === "done" && "bg-muted text-muted-foreground",
      )}
    >
      <span className="size-1.5 rounded-full bg-current" aria-hidden />
      {deliveryHealthLabel(health)}
    </span>
  );
}

function DeliveryRow({
  item,
  expanded,
  onToggle,
  onSynced,
  syncing,
}: {
  item: FeatureRequest;
  expanded: boolean;
  onToggle: () => void;
  onSynced: () => void;
  syncing: boolean;
}) {
  const stage = deliveryStageOf(item);
  const hl = deliveryHealthOf(item, stage);
  const dl = deliveryDeadlineOf(item);
  const n = dl ? daysUntil(dl) : null;
  const release = item.actual_delivery?.trim() || "";
  const relDiff = release && dl ? releaseVsDeadline(release, dl) : null;
  const pmName = item.pm_in_charge
    ? displayName(item.pm_in_charge, item.pm_in_charge_id ?? undefined)
    : null;
  const noteCount = item.pm_notes?.length ?? 0;

  return (
    <>
      <tr
        id={`dev-${item.id}`}
        aria-expanded={expanded}
        tabIndex={0}
        title="Show delivery details"
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        className={cn(
          "cursor-pointer align-top transition-colors hover:bg-muted/50",
          item.ceo_highlight && "shadow-[inset_4px_0_0_var(--primary)]",
          expanded && "bg-red-wash hover:bg-red-wash [&_td]:border-transparent",
        )}
      >
        <td className="max-w-[280px] border-b border-border px-3 py-3.5">
          <span className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
            LQ-{item.id}
          </span>
          <span className="mt-0.5 block truncate font-semibold">{deliveryTitleOf(item)}</span>
          <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <ProjectObjectiveChip objective={item.project_objective} />
            {item.ceo_highlight ? (
              <span className="rounded-full bg-primary px-1.5 py-px text-[10px] font-medium text-primary-foreground">
                CEO HIGHLIGHT
              </span>
            ) : null}
            {item.adhoc ? (
              <span className="rounded-sm border border-destructive px-1.5 py-px text-[10px] font-semibold tracking-wide text-destructive uppercase">
                Ad-hoc
              </span>
            ) : null}
            {noteCount > 0 ? (
              <span className="text-[11px] text-muted-foreground">
                {noteCount} {noteCount === 1 ? "note" : "notes"} from PM
              </span>
            ) : null}
          </span>
        </td>
        <td className="border-b border-border px-3 py-3.5">
          <span className="block whitespace-nowrap">{item.directorate?.trim() || "—"}</span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {displayName(item.requester, item.requester_id)}
          </span>
        </td>
        <td className="border-b border-border px-3 py-3.5 whitespace-nowrap">
          {pmName ? (
            pmName
          ) : (
            <span className="text-muted-foreground">Not assigned</span>
          )}
        </td>
        <td className="border-b border-border px-3 py-3.5">
          <StageChip stage={stage} />
          <StageStepper stage={stage} />
        </td>
        <td className="border-b border-border px-3 py-3.5">
          <HealthPill health={hl} />
        </td>
        <td className="border-b border-border px-3 py-3.5 whitespace-nowrap">
          {dl ? (
            <>
              <span
                className={cn(
                  "block font-mono text-[13px]",
                  hl === "overdue" && "font-semibold text-destructive",
                )}
              >
                {formatDate(dl)}
              </span>
              {stage !== "released" && n != null ? (
                <span
                  className={cn(
                    "mt-0.5 block text-xs",
                    hl === "overdue" ? "font-semibold text-destructive" : "text-muted-foreground",
                  )}
                >
                  {n < 0
                    ? `${Math.abs(n)} day${n === -1 ? "" : "s"} late`
                    : n === 0
                      ? "Due today"
                      : `in ${n} day${n === 1 ? "" : "s"}`}
                </span>
              ) : null}
            </>
          ) : (
            <span className="text-xs text-muted-foreground">No due date in Jira</span>
          )}
        </td>
        <td className="border-b border-border px-3 py-3.5 whitespace-nowrap">
          {release ? (
            <>
              <span className="block font-mono text-[13px]">{formatDate(release)}</span>
              {relDiff ? (
                <span
                  className={cn(
                    "mt-0.5 block text-xs",
                    relDiff.days > 0 ? "text-destructive" : "text-success",
                  )}
                >
                  {relDiff.label}
                </span>
              ) : null}
            </>
          ) : (
            <span className="text-xs text-muted-foreground">Not released yet</span>
          )}
        </td>
      </tr>
      {expanded ? (
        <tr className="bg-red-wash">
          <td colSpan={7} className="px-3 pb-5 pl-3 shadow-[inset_3px_0_0_var(--primary)]">
            <div className="flex flex-wrap items-center gap-2 py-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={syncing}
                onClick={(e) => {
                  e.stopPropagation();
                  onSynced();
                }}
              >
                <RefreshCw className={cn("size-3.5", syncing && "animate-spin")} />
                Sync this project
              </Button>
              {item.jira_issue_url ? (
                <a
                  href={item.jira_issue_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-medium text-primary underline-offset-4 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {item.jira_issue_key || "Open in Jira"}
                </a>
              ) : (
                <span className="text-xs text-muted-foreground">Not linked yet</span>
              )}
              <LinkJiraProjectButton
                requestId={item.id}
                currentKey={item.jira_issue_key}
                status={item.status}
                onLinked={() => onSynced()}
              />
            </div>
            {!item.jira_issue_key?.trim() ? (
              <CreateJiraProjectButton
                requestId={item.id}
                status={item.status}
                jiraIssueKey={item.jira_issue_key}
                className="mb-4"
                onCreated={() => onSynced()}
              />
            ) : null}
            <dl className="mb-4 grid gap-3 sm:grid-cols-4">
              {[
                ["Jira", item.jira_issue_key || "Not linked yet"],
                [
                  "Requested go-live",
                  item.expected_deadline ? formatDate(item.expected_deadline) : "—",
                ],
                ["Agreed timeline", item.agreed_date ? formatDate(item.agreed_date) : "Not set"],
                ["Last updated", formatDateTime(item.updated_at)],
              ].map(([k, v]) => (
                <div key={k} className="grid gap-0.5">
                  <dt className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                    {k}
                  </dt>
                  <dd className="text-sm">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <p className="mb-1 text-xs font-semibold tracking-[0.08em] text-red-ink uppercase">
                  Update history
                </p>
                <DeliveryUpdateHistory item={item} />
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold tracking-[0.08em] text-red-ink uppercase">
                  Decision log
                </p>
                <DeliveryDecisionLog item={item} />
              </div>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function DeliveryUpdateHistory({ item }: { item: FeatureRequest }) {
  const updates = item.delivery_updates ?? [];
  if (updates.length === 0) {
    return (
      <p className="text-[13px] text-muted-foreground">
        No updates from Jira yet. Sync loads stage changes from linked epics.
      </p>
    );
  }
  return (
    <ol className="divide-y divide-border border border-border">
      {updates.map((u) => {
        const rowDeadline = u.deadline || deliveryDeadlineOf(item);
        const healthFlip = String(u.id).startsWith("health:");
        return (
          <li key={u.id} className="grid gap-1 px-3 py-2.5 sm:grid-cols-[7.5rem_1fr]">
            <span className="font-mono text-[11px] text-muted-foreground">
              {formatDateTime(u.created_at)}
            </span>
            <span className="grid gap-1 text-[13px]">
              <span className="flex flex-wrap items-center gap-2">
                {u.stage ? <StageChip stage={u.stage} /> : null}
                {u.health ? <HealthPill health={u.health} /> : null}
                <span className="font-semibold">{healthFlip ? "Health sync" : "Jira sync"}</span>
              </span>
              {rowDeadline ? (
                <span className="text-xs text-muted-foreground">Deadline {formatDate(rowDeadline)}</span>
              ) : null}
              {healthFlip && u.note ? (
                <span className="text-xs text-muted-foreground whitespace-pre-wrap">{u.note}</span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function DeliveryDecisionLog({ item }: { item: FeatureRequest }) {
  const entries: Array<
    | { kind: "event"; at: string; day: string; event: FeatureRequestEvent }
    | { kind: "note"; at: string; day: string; note: PMNote }
  > = [];
  for (const event of item.events ?? []) {
    entries.push({ kind: "event", at: event.created_at, day: event.created_at.slice(0, 10), event });
  }
  for (const note of item.pm_notes ?? []) {
    entries.push({
      kind: "note",
      at: note.created_at || `${note.note_date}T00:00:00Z`,
      day: note.note_date,
      note,
    });
  }
  entries.sort((a, b) => b.day.localeCompare(a.day) || b.at.localeCompare(a.at));
  if (entries.length === 0) {
    return <p className="text-[13px] text-muted-foreground">No decision log yet.</p>;
  }
  return (
    <ol className="divide-y divide-border border border-border">
      {entries.map((entry) => {
        if (entry.kind === "note") {
          return (
            <li key={`n-${entry.note.id}`} className="grid gap-1 px-3 py-2.5 sm:grid-cols-[7.5rem_1fr]">
              <span className="font-mono text-[11px] text-muted-foreground">
                {formatDate(entry.note.note_date)}
              </span>
              <span className="text-[13px]">
                <span className="font-semibold">Note · {displayName(entry.note.author, entry.note.author_id)}</span>
                {entry.note.text?.trim() ? (
                  <span className="mt-0.5 block whitespace-pre-wrap text-muted-foreground">
                    {entry.note.text}
                  </span>
                ) : null}
                <PMNoteAttachmentList attachments={entry.note.attachments} />
              </span>
            </li>
          );
        }
        const ev = entry.event;
        const status = ev.to_status as FeatureRequestStatus;
        return (
          <li key={`e-${ev.id}`} className="grid gap-1 px-3 py-2.5 sm:grid-cols-[7.5rem_1fr]">
            <span className="font-mono text-[11px] text-muted-foreground">
              {formatDateTime(ev.created_at)}
            </span>
            <span className="flex flex-wrap items-center gap-2 text-[13px]">
              {ev.to_status === "pm_in_charge" ? (
                <>
                  <span className="font-semibold">PM in charge · {ev.note || "Removed"}</span>
                  <span className="text-muted-foreground">
                    by {displayName(ev.actor, ev.actor_id)}
                  </span>
                </>
              ) : ev.to_status === "jira_project" ? (
                <>
                  <span className="font-semibold">
                    Jira Project · {ev.note || "Updated"}
                  </span>
                  <span className="text-muted-foreground">
                    by {displayName(ev.actor, ev.actor_id)}
                  </span>
                </>
              ) : (
                <>
                  {["submitted", "under_review", "approved", "rejected", "deferred", "needs_clarification"].includes(
                    status,
                  ) ? (
                    <StatusBadge status={status} />
                  ) : (
                    <span>{formatStatusLabel(ev.to_status)}</span>
                  )}
                  <span className="font-semibold">{displayName(ev.actor, ev.actor_id)}</span>
                  {ev.note ? <span className="basis-full text-muted-foreground">{ev.note}</span> : null}
                </>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
